/*
 * The master inbox's list parts, drawn in the incumbent list's own anatomy (master-inbox plan, step
 * 3: docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md; DESIGN.md, "List rows"): the search
 * field, the filter bar, one conversation row, and the pages of 50 ("1–50 of 718", never infinite
 * scroll; Dawid, 2026-10-16).
 *
 * A row here is the reply agent's row with the master inbox's content: the subject and the time on
 * the first line, the lead and their company on the second, the preview, then a chip row holding
 * the label pill and the quiet attributes (the campaign while several are in view, "Waiting on
 * lead" where the seller wrote last). Type, spacing and colour come from the same classes
 * (`.lcr-row-*` in styles.css), so the two lists cannot drift apart.
 */
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { appCount, appDateTime, appDayMonth, appTime } from "../../src/app-dates.ts";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import {
  type AgentStatus,
  AgentStatusSchema,
  type InboxAgentState,
} from "../../src/inbox-v2/agent-contract.ts";
import type { InboxConversation } from "../../src/inbox-v2/contract.ts";
import { rowSubject } from "../../src/inbox-v2/row-subject.ts";
import { unansweredTip, waitedLabel } from "../../src/inbox-view.ts";
import { AGENT_CHIPS, AgentChip } from "../agent/agent-chip.tsx";
import { CheckBox } from "../bulk-bar.tsx";
import { AttachmentIcon, ClockIcon, PersonIcon } from "../icons.tsx";
import { type MenuRow, menuChoice, OptionMenu } from "../option-menu.tsx";
import { Tip } from "../tip.tsx";
import { assigneeWord } from "./assignment.tsx";
import { DataRequestMarker, LabelPill } from "./label-key.tsx";
import { LABEL_NAMES, RELABEL_CHOICES } from "./labels.ts";
import type { ListFilters } from "./list-prefs.ts";
import { MeetingRequestMarker } from "./meeting-marker.tsx";
import {
  campaignToken,
  HAS_FILES,
  type SearchToken,
  sameToken,
  withoutToken,
  withToken,
} from "./search-query.ts";

/** Today's clock, Yesterday, else the day and month, in the app's one date rule (`app-dates.ts`). */
export function rowTime(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  if (at >= midnight) return appTime(iso);
  if (midnight.getTime() - at.getTime() <= 86_400_000) return "Yesterday";
  return appDayMonth(iso);
}

/** A lead waiting longer than a working day: the incumbent row's amber clock. */
const WAITING_LONG_MS = 24 * 60 * 60 * 1000;

/** The Unanswered clock's tone: amber once the lead has waited longer than a working day. */
export function unansweredTone(since: string, now: Date): "warn" | "plain" {
  return now.getTime() - Date.parse(since) >= WAITING_LONG_MS ? "warn" : "plain";
}

/*
 * The row's clock, as the reply agent's row draws it: "Unanswered 4 d" while the lead's reply has gone
 * unanswered (`waitingSince`), amber after a working day; else the time of the newest message. In a
 * row narrower than 440px, the default 400px list and a phone among them, the word gives way to the
 * clock mark so the subject keeps its room: "28 h", still "Unanswered 28 h" to a screen reader, with
 * the same tip.
 */
function RowClock(props: { row: InboxConversation }) {
  const { row } = props;
  const since = row.waitingSince ?? null;
  if (since === null)
    return (
      <time
        className="lcr-row-time"
        dateTime={row.lastMessageAt}
        title={appDateTime(row.lastMessageAt)}
      >
        {rowTime(row.lastMessageAt)}
      </time>
    );
  const now = new Date();
  return (
    <time
      className="lcr-row-time"
      data-tone={unansweredTone(since, now)}
      dateTime={since}
      title={unansweredTip(appDateTime(since))}
    >
      <ClockIcon size={14} className="lcr-row-clock-mark" />
      <span className="lcr-row-clock-word">Unanswered </span>
      {waitedLabel(since, now)}
    </time>
  );
}

export function personName(row: InboxConversation): string {
  return row.leadName ?? row.leadAddress?.split("@")[0] ?? row.company ?? "Unknown sender";
}

/**
 * The row's label (`label-key.tsx`): its key's dot and its word, dashed where the AI was unsure; then
 * Meeting request where the newest reply asks to meet (LEA-494), then Data request.
 */
function RowLabel(props: { row: InboxConversation }) {
  const label = props.row.label;
  return (
    <>
      {label?.code && (
        <LabelPill code={label.code} uncertain={label.uncertain} className="lcr-row-label" />
      )}
      {props.row.meetingRequest === true && <MeetingRequestMarker />}
      {props.row.dataRequest === true && <DataRequestMarker />}
    </>
  );
}

const STATUS_NAMES = {
  meeting: "Meeting requests",
  needs_reply: "Needs reply",
  waiting: "Waiting on lead",
} as const;
const DATE_CHOICES = [7, 30, 90] as const;

type FilterCounts = {
  meeting?: number | undefined;
  needs_reply?: number | undefined;
  waiting?: number | undefined;
};

/** A choice's total, read as "1 conversation" or "7 conversations". */
const counted = (count: number | undefined) =>
  count === undefined
    ? {}
    : { count: { value: count, noun: count === 1 ? "conversation" : "conversations" } };

/** A master status's total, or a bar in its place while the counts are on their way. */
const statusCount = (count: number | undefined, pending: boolean) =>
  pending ? { count: "pending" as const } : counted(count);

/** The Reply agent group's words: the row chips' own (`AGENT_CHIPS`), then the two promises. */
const AGENT_STATUS_NAMES: Record<AgentStatus, string> = {
  draft_ready: AGENT_CHIPS.draft_ready.label,
  ready_to_call: AGENT_CHIPS.ready_to_call.label,
  needs_answer: AGENT_CHIPS.needs_answer.label,
  drafting: AGENT_CHIPS.drafting.label,
  no_draft: AGENT_CHIPS.no_draft.label,
  sending: AGENT_CHIPS.sending.label,
  check_bison: AGENT_CHIPS.check_bison.label,
  answer_owed: "Owes answer",
  agreed: "Agreed, not completed",
};

/**
 * What each Reply agent state means, in one line under its name (step 10, round 3), as the master
 * statuses above it say theirs: read from `agent-state.ts` and `agent-status.ts`, which decide them.
 */
const AGENT_STATUS_DETAILS: Record<AgentStatus, string> = {
  ready_to_call: "A phone lead is ready for your call",
  draft_ready: "The agent's draft is ready to use",
  needs_answer: "The draft waits on your answer",
  drafting: "The agent is writing the draft",
  no_draft: "Your move, with no draft from the agent",
  sending: "Your reply is going out through Bison",
  check_bison: "A send's outcome is unknown: check Bison",
  answer_owed: "An answer promised to the lead is still owed",
  agreed: "The lead said yes; the goal is not completed",
};

/** The reply agent's part of the filter bar, for a person whose switch is on (`/access`). */
export type AgentFilter = {
  /** Each status's total under the other filters, once a status is chosen; null before. */
  counts: Readonly<Record<AgentStatus, number>> | null;
};

type StatusChoice = "all" | "meeting" | "needs_reply" | "waiting" | "any_agent" | AgentStatus;

/** What Status narrows to, in its own words: "Needs reply · Draft ready"; undefined for neither. */
function statusSummary(filters: ListFilters): string | undefined {
  const names = [
    ...(filters.status ? [STATUS_NAMES[filters.status]] : []),
    ...(filters.agent ? [AGENT_STATUS_NAMES[filters.agent]] : []),
  ];
  return names.length ? names.join(" · ") : undefined;
}

/**
 * The Status filter's choices: any, the two master statuses with their totals, and, for a person
 * with the reply agent, its states in a group of their own, opened in the menu's place. The agent
 * state is a second choice beside the master status (step 9): both narrow the list at once.
 */
function statusOptions(
  counts: FilterCounts | undefined,
  agent: AgentFilter | undefined,
  filters: ListFilters,
  countsPending: boolean,
): MenuRow<StatusChoice>[] {
  // The master totals are the whole workspace's; beside an agent state's narrowed totals they would
  // read as the same kind of number, so they wait until no agent state is on.
  const master = filters.agent === null ? counts : undefined;
  const pending = filters.agent === null && countsPending;
  return [
    { value: "all", label: "Any status" },
    // Meeting requests and Needs reply split what needs an answer, as their rail rows do (LEA-494).
    {
      value: "meeting",
      label: STATUS_NAMES.meeting,
      detail: "Interested replies that ask to meet",
      ...statusCount(master?.meeting, pending),
    },
    {
      value: "needs_reply",
      label: STATUS_NAMES.needs_reply,
      detail: "The lead's latest reply is unanswered, apart from meeting requests",
      ...statusCount(master?.needs_reply, pending),
    },
    {
      value: "waiting",
      label: STATUS_NAMES.waiting,
      detail: "We wrote last",
      ...statusCount(master?.waiting, pending),
    },
    ...(agent
      ? [
          {
            key: "reply_agent",
            label: "Reply agent",
            value: filters.agent ?? ("any_agent" as const),
            // A menu whose only choice is an agent state opens on it.
            open: filters.agent !== null && filters.status === null,
            submenu: [
              { value: "any_agent" as const, label: "Any agent state" },
              ...AgentStatusSchema.options.map((status) => ({
                value: status,
                label: AGENT_STATUS_NAMES[status],
                detail: AGENT_STATUS_DETAILS[status],
                ...counted(agent.counts?.[status]),
              })),
            ],
          },
        ]
      : []),
  ];
}

/**
 * A filter that opens its choices: drawn as the bar's other filters are, inked while it narrows the
 * list, and saying what it narrows to. Its first choice puts it back.
 */
function FilterMenu<T extends string>(props: {
  label: string;
  value: T | "all";
  options: readonly MenuRow<T | "all">[];
  onChange: (value: T | "all") => void;
  /** What it narrows to where a group's own choice joins the menu's (Status); else the choice. */
  summary?: string | undefined;
  /** Said under the choices: the campaigns still on their way, or that there are none. */
  after?: ReactNode;
}) {
  const current = menuChoice(props.options, props.value);
  const on = props.value !== "all" || props.summary !== undefined;
  return (
    <OptionMenu
      label={props.label}
      icon={null}
      className="lcr-filter-menu"
      value={props.value}
      options={props.options}
      display={on ? (props.summary ?? current?.short ?? current?.label) : props.label}
      pressed={on}
      {...(props.summary ? { summary: props.summary } : {})}
      after={props.after}
      onChange={props.onChange}
    />
  );
}

/** What the Unsure label chip holds, on hover and keyboard focus and to a screen reader. */
const UNSURE_LABEL_TIP =
  "Conversations whose label the AI was not sure of. Confirm each label, or choose another.";
/** The tip's widest, with the list's edge beyond it: past that it hangs from the chip's other end. */
const FILTER_TIP_ROOM = 276;

/** A filter that is on or off: the summary's squared bucket, inked with a cross while on. */
function FilterToggle(props: {
  on: boolean;
  label: string;
  count?: number | undefined;
  onToggle: () => void;
  icon?: React.ReactNode;
  /** What the filter means, said in the app's tip (`tip.tsx`) and as the button's description. */
  tip?: string;
}) {
  const tipId = useId();
  const button = (
    <button
      type="button"
      className="lcr-summary-bucket lcr-filter-toggle"
      aria-pressed={props.on}
      // Read as "Unsure label, 5": a screen reader does not hear the margin before the count.
      aria-label={props.count ? `${props.label}, ${appCount(props.count)}` : undefined}
      aria-describedby={props.tip ? tipId : undefined}
      onClick={props.onToggle}
    >
      {props.icon}
      {props.label}
      {props.count ? <span className="lcr-filter-count">{appCount(props.count)}</span> : null}
      {props.on && <span className="lcr-chip-x" aria-hidden="true" />}
    </button>
  );
  return props.tip ? (
    <FilterTipped id={tipId} tip={props.tip}>
      {button}
    </FilterTipped>
  ) : (
    button
  );
}

/*
 * A filter chip with its tip under it (`.lcr-filter-tip`), from the chip's start, or from its end
 * where the list's edge would cut it off. The tip is the eye's; the hidden copy is the description.
 */
function FilterTipped(props: { id: string; tip: string; children: ReactNode }) {
  const wrap = useRef<HTMLSpanElement>(null);
  const [end, setEnd] = useState(false);
  // Placed as the pointer or the focus arrives, as `tip.tsx` listens: the wrapper is not a control.
  useEffect(() => {
    const element = wrap.current;
    if (!element) return;
    const place = (): void => {
      const edge = element.parentElement?.getBoundingClientRect().right;
      if (edge !== undefined) setEnd(edge - element.getBoundingClientRect().left < FILTER_TIP_ROOM);
    };
    element.addEventListener("pointerenter", place);
    element.addEventListener("focusin", place);
    return () => {
      element.removeEventListener("pointerenter", place);
      element.removeEventListener("focusin", place);
    };
  }, []);
  return (
    <span ref={wrap} className="lcr-filter-tipped" data-tip-end={end || undefined}>
      {props.children}
      <Tip className="lcr-filter-tip">{props.tip}</Tip>
      <span id={props.id} className="lcr-visually-hidden">
        {props.tip}
      </span>
    </span>
  );
}

type Campaigns = readonly { id: string; name: string; count?: number }[];

/** How many campaign rows a loading menu draws in the shape of the rows to come. */
const CAMPAIGN_PLACEHOLDERS = [0, 1, 2] as const;

/**
 * Under the Campaign menu's choices (design review "Loading and empty", P2): three rows in the real
 * rows' shape while the campaigns are on their way, one quiet line for a screen reader, and a real
 * empty said as one. A workspace with one campaign has no Campaign filter at all.
 */
function CampaignsNote(props: { campaigns: Campaigns | "loading" }) {
  if (props.campaigns === "loading")
    return (
      <>
        <p className="lcr-visually-hidden" role="status">
          Loading campaigns
        </p>
        <div className="lcr-opening lcr-menu-placeholders" aria-hidden="true">
          {CAMPAIGN_PLACEHOLDERS.map((row) => (
            <span key={row} className="lcr-menu-placeholder">
              <span className="lcr-skeleton" />
              <span className="lcr-skeleton" />
            </span>
          ))}
        </div>
      </>
    );
  return props.campaigns.length === 0 ? <p className="lcr-menu-note">No campaigns yet</p> : null;
}

/**
 * The filter bar, in the order Dawid settled on 2026-10-17: Campaign, Status, Unseen, Files, Date
 * (All time until chosen). Label follows where the list is not already one (step 2 of
 * docs/2026-10-27-inbox-rail-scope-and-counts-plan.md), then Unsure label, the labels Jev was unsure
 * of, while there are any.
 * Status is left out where the list already is a status (the Needs reply view).
 *
 * Campaign and Files write search operators (`campaign-id:"…"`, `has:attachment`), the same
 * tokens the typeahead writes and the search field shows, so either place can take them off. The
 * others have no operator in the API's search and stay the list's own filters.
 */
export function FilterChips(props: {
  filters: ListFilters;
  tokens: readonly SearchToken[];
  onTokens: (next: SearchToken[]) => void;
  /** The workspace's campaigns; "loading" while the counts are on their way, null if they failed. */
  campaigns: Campaigns | "loading" | null;
  counts?: FilterCounts | undefined;
  /** The counts' first answer is on its way: the Status menu holds each total's place. */
  countsPending?: boolean;
  /** False where the view itself fixes the status. */
  status?: boolean;
  /** False where the view itself is a label (a rail label row) or Needs reply. */
  label?: boolean;
  /** How many labels wait for a rep; shown on the Unsure label chip when there are any. */
  checkCount?: number | undefined;
  /** The Status filter's Reply agent group; absent where the person's switch is off. */
  agent?: AgentFilter | undefined;
  onChange: (next: ListFilters) => void;
}) {
  const { filters, tokens } = props;
  const set = (patch: Partial<ListFilters>) => props.onChange({ ...filters, ...patch });
  // The master status and the agent state are chosen apart, and each first choice puts its own back.
  const chooseStatus = (value: StatusChoice) => {
    if (value === "all") return set({ status: null });
    if (value === "meeting" || value === "needs_reply" || value === "waiting")
      return set({ status: value });
    set({ agent: value === "any_agent" ? null : value });
  };
  const campaign = campaignToken(tokens);
  const files = tokens.some((token) => sameToken(token, HAS_FILES));
  const chooseCampaign = (id: string) => {
    const rest = tokens.filter((token) => token.kind !== "campaign-id");
    props.onTokens(id === "all" ? rest : [...rest, { kind: "campaign-id", value: id }]);
  };
  return (
    <div className="lcr-summary lcr-filter-bar">
      {props.campaigns !== null && props.campaigns.length !== 1 && (
        <FilterMenu
          label="Campaign"
          value={campaign?.value ?? "all"}
          options={[
            { value: "all", label: "All campaigns" },
            ...(props.campaigns === "loading" ? [] : props.campaigns).map((campaign) => ({
              value: campaign.id,
              label: campaign.name,
              ...counted(campaign.count),
            })),
          ]}
          after={<CampaignsNote campaigns={props.campaigns} />}
          onChange={chooseCampaign}
        />
      )}
      {props.status !== false && (
        <FilterMenu<StatusChoice>
          label="Status"
          value={filters.status ?? "all"}
          options={statusOptions(props.counts, props.agent, filters, props.countsPending === true)}
          summary={statusSummary(filters)}
          onChange={chooseStatus}
        />
      )}
      <FilterToggle
        on={filters.unread}
        label="Unseen"
        onToggle={() => set({ unread: !filters.unread })}
      />
      <FilterToggle
        on={files}
        label="Files"
        icon={<AttachmentIcon size={14} />}
        onToggle={() =>
          props.onTokens(files ? withoutToken(tokens, HAS_FILES) : withToken(tokens, HAS_FILES))
        }
      />
      <FilterMenu
        label="Date"
        value={filters.days === null ? "all" : String(filters.days)}
        options={[
          { value: "all", label: "All time" },
          ...DATE_CHOICES.map((days) => ({ value: String(days), label: `Last ${days} days` })),
        ]}
        onChange={(value) => set({ days: value === "all" ? null : Number(value) })}
      />
      {props.label !== false && (
        <FilterMenu<InboxLabelCode>
          label="Label"
          value={filters.label === null || filters.label === "none" ? "all" : filters.label}
          options={[
            { value: "all", label: "All labels" },
            ...RELABEL_CHOICES.map((code) => ({ value: code, label: LABEL_NAMES[code] })),
          ]}
          onChange={(value) => set({ label: value === "all" ? null : value })}
        />
      )}
      {(props.checkCount ?? 0) > 0 || filters.check ? (
        <FilterToggle
          on={filters.check}
          label="Unsure label"
          count={props.checkCount}
          tip={UNSURE_LABEL_TIP}
          onToggle={() => set({ check: !filters.check })}
        />
      ) : null}
    </div>
  );
}

/*
 * A touch held on a row ticks it instead of opening it, as the reply agent's list does; moving more
 * than a finger's wobble is a scroll, not a hold.
 */
function useLongPress(onLongPress: () => void) {
  const timer = useRef<number | null>(null);
  const held = useRef(false);
  const start = useRef({ x: 0, y: 0 });
  const release = (): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );
  return {
    /** Whether the click that follows a hold should be swallowed; reading it clears it. */
    consumed: (): boolean => {
      const was = held.current;
      held.current = false;
      return was;
    },
    handlers: {
      onPointerDown: (event: React.PointerEvent) => {
        if (event.pointerType !== "touch") return;
        held.current = false;
        release();
        start.current = { x: event.clientX, y: event.clientY };
        timer.current = window.setTimeout(() => {
          held.current = true;
          onLongPress();
        }, 500);
      },
      onPointerUp: release,
      onPointerCancel: release,
      onPointerMove: (event: React.PointerEvent) => {
        const moved = Math.hypot(event.clientX - start.current.x, event.clientY - start.current.y);
        if (event.pointerType === "touch" && moved > 8) release();
      },
    },
  };
}

/** The quiet words after the pill: who wrote last, until when it sleeps, which campaign. */
function rowAttributes(row: InboxConversation, showCampaign: boolean): string[] {
  return [
    row.lastFrom === "seller" && row.doneAt === null ? "Waiting on lead" : null,
    row.snooze ? `Snoozed until ${appDayMonth(row.snooze.until)}` : null,
    showCampaign ? row.campaign.name : null,
  ].filter((attribute) => attribute !== null);
}

/** A row's own Snooze: a pointer's shortcut to what H and the bulk bar also open. */
function RowSnooze(props: { name: string; open: boolean; onSnooze: () => void }) {
  return (
    <button
      type="button"
      className="lcr-icon-button lcr-row-snooze"
      tabIndex={-1}
      aria-label={`Snooze ${props.name}`}
      aria-haspopup="dialog"
      aria-expanded={props.open || undefined}
      aria-keyshortcuts="H"
      title="Snooze (H)"
      onClick={props.onSnooze}
    >
      <ClockIcon size={16} />
    </button>
  );
}

/**
 * The row's one line of chips: the label (the lead's meaning), the reply agent's state where the
 * person has the agent, then the quiet attributes, led by who the row is assigned to. Nobody is
 * said by no word: a workspace without rules would otherwise print "Unassigned" on every row.
 */
function RowChips(props: {
  row: InboxConversation;
  agent: InboxAgentState | null;
  showCampaign: boolean;
  you?: string | null | undefined;
}) {
  const { row } = props;
  const attributes = rowAttributes(row, props.showCampaign);
  const assignee = row.assignee ? assigneeWord(row.assignee, props.you ?? null) : null;
  return (
    <span className="lcr-row-chips">
      <RowLabel row={row} />
      {props.agent && <AgentChip state={props.agent} className="lcr-row-agent" />}
      {(attributes.length > 0 || row.hasFiles || assignee) && (
        <span className="lcr-row-attributes">
          {row.hasFiles && (
            <span className="lcr-row-files">
              <AttachmentIcon size={13} />
              <span className="lcr-visually-hidden">Has files. </span>
            </span>
          )}
          {assignee && (
            <span className="lcr-row-assignee">
              <PersonIcon size={13} />
              <span className="lcr-visually-hidden">Assigned to </span>
              {assignee}
              {attributes.length > 0 ? " · " : ""}
            </span>
          )}
          {attributes.join(" · ")}
        </span>
      )}
    </span>
  );
}

/**
 * The subject, without the campaign's name where the row's chips already show it, so the words that
 * tell a campaign's rows apart are the ones read first (`row-subject.ts`). The whole subject is the
 * pointer's tip.
 */
function RowSubject(props: { row: InboxConversation; showCampaign: boolean }) {
  const { row } = props;
  const shown = rowSubject(row.subject, row.campaign.name, props.showCampaign);
  return (
    <span className="lcr-row-subject" title={shown === row.subject ? undefined : row.subject}>
      {shown}
    </span>
  );
}

/** The lead's company, or their address's domain where the company is not on file. */
export function leadCompany(row: InboxConversation): string | null {
  return row.company ?? row.leadAddress?.split("@")[1] ?? null;
}

/** "Rowan · Synthetic Freight": who wrote, and their company or their address's domain. */
function whoLine(row: InboxConversation): string {
  return [personName(row), leadCompany(row)].filter(Boolean).join(" · ");
}

export function ConversationRow(props: {
  row: InboxConversation;
  showCampaign: boolean;
  focused: boolean;
  open: boolean;
  selected: boolean;
  selecting: boolean;
  /** No box to tick and no hold to select: a row kept from an older list is read only. */
  readOnly?: boolean;
  /** The reply agent's state, for a person with the switch on (step 8d); absent draws nothing. */
  agent?: InboxAgentState | null;
  /** The viewer, whose own assignment the row says as "You"; absent, every assignee is named. */
  you?: string | null;
  /** A copy in the Highlights strip; the list below keeps the row J and K move through. */
  pinned?: boolean;
  /**
   * 0 on the row Tab lands on, -1 on the rest: the list is one Tab stop, and J, K and the arrows
   * move within it (step 10, round 2). Absent, the row is a plain button.
   */
  tabIndex?: 0 | -1;
  onOpen: () => void;
  onToggle: (shiftKey: boolean) => void;
  onLongPress: () => void;
  /** The row's own Snooze, shown on hover where a pointer can hover; H does the same. */
  onSnooze?: (() => void) | undefined;
  /** Snooze's choices, open on this row. */
  snoozeMenu?: ReactNode;
}) {
  const { row } = props;
  const press = useLongPress(props.readOnly ? () => undefined : props.onLongPress);
  const who = whoLine(row);
  // The strip's copy is named apart from the row below it, which keeps the plain name.
  const pinned = props.pinned ? "Pinned: " : "";
  return (
    <div
      className="lcr-row-wrap"
      data-checked={props.selected || undefined}
      data-cursor={(props.focused && !props.open) || undefined}
      data-menu={props.snoozeMenu ? "" : undefined}
    >
      {!props.readOnly && (
        <CheckBox
          className="lcr-row-check"
          checked={props.selected}
          label={`Select ${pinned.toLowerCase()}${row.subject}`}
          tabbable={props.selecting}
          onToggle={props.onToggle}
        />
      )}
      <button
        type="button"
        className="lcr-row"
        data-row-id={row.id}
        data-pinned={props.pinned || undefined}
        data-unread={row.unread || undefined}
        tabIndex={props.tabIndex}
        aria-current={props.open}
        onClick={(event) => {
          if (press.consumed()) return event.preventDefault();
          props.onOpen();
        }}
        {...press.handlers}
      >
        <span className="lcr-row-line">
          {pinned && <span className="lcr-visually-hidden">{pinned}</span>}
          {row.unread && (
            <span className="lcr-unread-dot">
              <span className="lcr-visually-hidden">Unseen: </span>
            </span>
          )}
          <RowSubject row={row} showCampaign={props.showCampaign} />
          <RowClock row={row} />
        </span>
        <span className="lcr-row-lead" title={row.leadAddress ?? undefined}>
          {who}
        </span>
        {row.snippet && <span className="lcr-row-preview">{row.snippet}</span>}
        <RowChips
          row={row}
          agent={props.agent ?? null}
          showCampaign={props.showCampaign}
          you={props.you}
        />
      </button>
      {props.onSnooze && (
        <RowSnooze
          name={`${pinned.toLowerCase()}${row.subject}`}
          open={Boolean(props.snoozeMenu)}
          onSnooze={props.onSnooze}
        />
      )}
      {props.snoozeMenu}
    </div>
  );
}
